import { useState } from 'react';
import {
  AppButton,
  AppChip,
  AppDateField,
  AppDivider,
  AppEmptyState,
  AppFormDialog,
  AppMenuSheet,
  AppReorderable,
  AppScreenScaffold,
  AppSwipeRow,
  AppTextField,
  AppTopBar,
  Icon,
  IconButton,
} from '@core/designsystem';
import type { CaseType } from '@core/domain/model';
import { CaseRow } from './CaseRow';
import { ClientPicker } from './ClientPicker';
import { NewCaseDialog } from './NewCaseDialog';
import { FILTERS, SORTS, type CaseListItem, type CasesState } from './types';
import './cases.css';

export interface CasesActions {
  setFilter: (filter: CasesState['filter']) => void;
  setSort: (sort: CasesState['sort']) => void;
  setClientFilter: (id: string | null) => void;
  toggleTag: (id: string) => void;
  finishReordering: () => void;
  togglePinned: (id: string, isPinned: boolean) => Promise<void>;
  reorder: (orderedIds: string[]) => Promise<void>;
  setDeadline: (caseId: string, title: string, deadlineAt: number) => Promise<void>;
  createCase: (draft: {
    title: string;
    type: CaseType;
    typeOther: string | null;
    clientId: string | null;
  }) => Promise<unknown>;
  createClient: (draft: {
    fullName: string;
    phone: string | null;
    email: string | null;
  }) => Promise<string | null>;
}

/**
 * Экран «Дела» — корневой экран приложения и первое, что видит юрист.
 *
 * Чистая функция состояния: ни базы, ни навигации внутри. Всё, что он умеет, —
 * показать список и позвать действие ([ARCHITECTURE.md](docs/ARCHITECTURE.md),
 * правило 3).
 */
export function CasesScreen({
  state,
  actions,
  onOpenCase,
  onScan,
  onOpenSettings,
  creating = false,
  onCreatingChange,
}: {
  state: CasesState;
  actions: CasesActions;
  onOpenCase: (id: string) => void;
  /** Свайп вправо ведёт в документы дела: там живёт съёмка ([D-10]). */
  onScan: (id: string) => void;
  /** Настройки: хранилище, архив, тема — «Структура» §14. */
  onOpenSettings: () => void;
  /** Открыт ли диалог создания: «плюс» живёт в каркасе, а диалог — здесь. */
  creating?: boolean;
  onCreatingChange?: (creating: boolean) => void;
}) {
  const [menuFor, setMenuFor] = useState<CaseListItem | null>(null);
  const [deadlineFor, setDeadlineFor] = useState<CaseListItem | null>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [clientFilterOpen, setClientFilterOpen] = useState(false);

  const clientFilterName = state.clients.find(
    (client) => client.id === state.clientFilterId,
  )?.name;
  const clientFilterLabel = clientFilterName ? `Клиент: ${clientFilterName}` : 'Клиент…';

  /** Включён ли хоть какой-то отбор: от этого зависит, что говорить о пустом списке. */
  const isFiltered =
    state.filter !== 'all' || state.clientFilterId !== null || state.tagFilters.some((t) => t.isSelected);

  const showAll = () => {
    actions.setFilter('all');
    actions.setClientFilter(null);
    state.tagFilters.filter((tag) => tag.isSelected).forEach((tag) => actions.toggleTag(tag.id));
  };

  const sortLabel = SORTS.find((item) => item.key === state.sort)?.label ?? '';

  const rowActions = (item: CaseListItem) => ({
    start: {
      icon: 'document_scanner' as const,
      label: 'Сканировать',
      onAction: () => onScan(item.id),
    },
    end: {
      icon: 'schedule' as const,
      label: 'Срок',
      onAction: () => setDeadlineFor(item),
    },
  });

  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title="Дела"
          root
          actions={
            <>
              <IconButton
                icon="tune"
                label="Сортировка"
                onClick={() => setSortOpen(true)}
              />
              {/*
                Настройки открываются отсюда — с корневого экрана, как на Android.
                До шага 19 в них можно было попасть только адресом /settings: экран
                был, а двери в него не было, и вместе с ней недоступен был архив.
              */}
              <IconButton icon="settings" label="Настройки" onClick={onOpenSettings} />
            </>
          }
        />
      }
      overlay={
        <>
          {sortOpen ? (
            <AppMenuSheet
              title="Порядок дел"
              onDismiss={() => setSortOpen(false)}
              items={SORTS.map((item) => ({
                icon: item.key === state.sort ? ('check' as const) : ('swap_vert' as const),
                label: item.label,
                onSelect: () => actions.setSort(item.key),
              }))}
            />
          ) : null}

          {menuFor ? (
            <AppMenuSheet
              title={menuFor.title}
              onDismiss={() => setMenuFor(null)}
              items={[
                {
                  icon: 'document_scanner',
                  label: 'Сканировать документ',
                  onSelect: () => onScan(menuFor.id),
                },
                {
                  icon: 'schedule',
                  label: 'Поставить срок',
                  onSelect: () => setDeadlineFor(menuFor),
                },
                {
                  icon: 'keep',
                  label: menuFor.isPinned ? 'Открепить' : 'Закрепить',
                  onSelect: () => void actions.togglePinned(menuFor.id, !menuFor.isPinned),
                },
              ]}
            />
          ) : null}

          {creating ? (
            <NewCaseDialog
              clients={state.clients}
              onDismiss={() => onCreatingChange?.(false)}
              onCreate={(draft) => {
                void actions.createCase(draft);
                onCreatingChange?.(false);
              }}
              onCreateClient={actions.createClient}
            />
          ) : null}

          {clientFilterOpen ? (
            <ClientPicker
              clients={state.clients}
              title="Дела клиента"
              clearLabel="Все клиенты"
              creatable={false}
              onDismiss={() => setClientFilterOpen(false)}
              onSelect={(id) => {
                actions.setClientFilter(id);
                setClientFilterOpen(false);
              }}
              onCreate={() => undefined}
            />
          ) : null}

          {deadlineFor ? (
            <DeadlineDialog
              caseTitle={deadlineFor.title}
              onDismiss={() => setDeadlineFor(null)}
              onConfirm={(title, at) => {
                void actions.setDeadline(deadlineFor.id, title, at);
                setDeadlineFor(null);
              }}
            />
          ) : null}
        </>
      }
    >
      <div className="cases__filters">
        {FILTERS.map((item) => (
          <AppChip
            key={item.key}
            label={item.label}
            selected={state.filter === item.key}
            onClick={() => actions.setFilter(item.key)}
          />
        ))}
        {state.tagFilters.map((tag) => (
          <AppChip
            key={tag.id}
            label={tag.name}
            selected={tag.isSelected}
            onClick={() => actions.toggleTag(tag.id)}
          />
        ))}
        {/*
          Отбор по клиенту — чипом, но список клиентов открывается отдельно: их сотни,
          и чипом каждого не выберешь. Выбранный клиент виден прямо на чипе, повторное
          нажатие снимает отбор — так же, как на Android.
        */}
        <AppChip
          label={clientFilterLabel}
          selected={state.clientFilterId !== null}
          onClick={() =>
            state.clientFilterId === null
              ? setClientFilterOpen(true)
              : actions.setClientFilter(null)
          }
        />
      </div>

      <div className="cases__toolbar">
        <button type="button" className="cases__sort" onClick={() => setSortOpen(true)}>
          <Icon name="swap_vert" size={20} />
          {sortLabel}
        </button>
      </div>

      {state.isReordering ? (
        <div className="cases__reorder-hint">
          <span>Перетащите дела в нужном порядке</span>
          <AppButton variant="text" onClick={actions.finishReordering}>
            Готово
          </AppButton>
        </div>
      ) : null}

      {state.cases.length === 0 && !state.isLoading ? (
        /*
         * Пустой список отвечает, **почему** он пуст, и предлагает следующий шаг.
         * «Дел пока нет» под включённым фильтром — неправда: дела есть, их скрыл отбор.
         */
        isFiltered ? (
          <AppEmptyState
            icon="folder"
            text="По этому отбору дел нет"
            action={
              <AppButton variant="text" onClick={showAll}>
                Показать все дела
              </AppButton>
            }
          />
        ) : (
          <AppEmptyState
            icon="folder"
            text="Дел пока нет"
            action={
              <AppButton variant="text" onClick={() => onCreatingChange?.(true)}>
                Создать дело
              </AppButton>
            }
          />
        )
      ) : null}

      <div className="cases__list">
        {state.isReordering ? (
          <AppReorderable
            items={state.cases}
            keyOf={(item) => item.id}
            onReorder={(ids) => void actions.reorder(ids)}
            renderRow={(item, handle) => (
              <CaseRow item={item} onOpen={() => onOpenCase(item.id)} handle={handle} />
            )}
          />
        ) : (
          state.cases.map((item) => (
            <div key={item.id}>
              <AppSwipeRow
                start={rowActions(item).start}
                end={rowActions(item).end}
                onLongPress={() => setMenuFor(item)}
              >
                <CaseRow
                  item={item}
                  onOpen={() => onOpenCase(item.id)}
                  onMenu={() => setMenuFor(item)}
                />
              </AppSwipeRow>
              <AppDivider withIcon />
            </div>
          ))
        )}
      </div>
    </AppScreenScaffold>
  );
}

/**
 * «Поставить срок» — задача с датой, привязанная к делу.
 *
 * Отдельного экрана не нужно: юрист ставит срок в две строки, а разбирается с ним
 * потом в «Задачах». Дата по умолчанию — завтра: сегодняшний срок ставят редко,
 * а листать календарь назад никто не будет.
 */
function DeadlineDialog({
  caseTitle,
  onDismiss,
  onConfirm,
}: {
  caseTitle: string;
  onDismiss: () => void;
  onConfirm: (title: string, deadlineAt: number) => void;
}) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(tomorrow.toISOString().slice(0, 10));

  const confirm = () => {
    const at = new Date(`${date}T12:00`);
    if (Number.isNaN(at.getTime())) return;

    onConfirm(title.trim() || 'Срок', at.getTime());
  };

  return (
    <AppFormDialog
      title="Поставить срок"
      onDismiss={onDismiss}
      confirm={{ label: 'Готово', onClick: confirm }}
    >
      <AppTextField
        value={title}
        onChange={setTitle}
        label="Что сделать"
        placeholder={caseTitle}
        autoFocus
      />
      <AppDateField value={date} onChange={setDate} label="Срок" />
    </AppFormDialog>
  );
}
